// Format content to handle escaped newlines and ensure proper Markdown bullet points
export const formatContentForMarkdown = (content: string) => {
  // Replace escaped newlines with actual newlines and ensure proper bullet point format
  return content
    .replace(/\\\\n/g, "\n")
    .split("\n")
    .map((line) => {
      line = line.trim();
      // If line starts with "- ", it's already a bullet point
      if (line.startsWith("- ")) {
        return line;
      }
      // If line starts with a bullet point but no space, add the space
      if (line.startsWith("-")) {
        return `- ${line.slice(1).trim()}`;
      }
      // If no bullet point, add one
      return `- ${line}`;
    })
    .join("\n");
};

const highlightedTextStyles =
  "prose:text-orange-700 prose-headings:text-orange-700 prose-strong:text-orange-700 prose-em:text-orange-700 prose-li:marker:text-orange-700 prose-a:text-orange-700 prose-blockquote:text-orange-700";

// Helper function to wrap a missing list item and its sub-bullets in a div
const wrapMissingListItem = (lines: string[], baseIndent: number) => {
  // Join the lines with newlines
  let content = lines.join("\n");
  content = content.replace("[MISSING]", "**[MISSING]**");
  const indent = " ".repeat(baseIndent);

  // the indent is key before because it properly spaces out the div so it matches the indentation of the list it is in
  // the indent is also added to the end of the div to ensure the div is properly closed
  // the \n is added to ensure the div is properly closed and the next markdown item begins on a new line
  return `${indent}<div class="bg-orange-100 border-l-2 text-orange-700 border-orange-500 px-2 py-1 rounded-md my-1"><div class="prose-sm max-w-none prose-ul:mt-1 prose-li:mt-0.5 ${highlightedTextStyles}">\n\n${content}\n\n${indent}</div></div>`;
};

export const processMarkdownContentForEditor = (content: string) => {
  if (!content) return "";

  // First, handle section headers with [MISSING]
  // Split content by markdown headers (lines starting with #)
  const sections = content.split(/\n(?=#{1,6}\s+)/);
  const missingHeaderRegex = /^#{1,6}\s+.*\[MISSING\]/;

  // Process all sections
  // Split the content into lines for processing (make sure to skip the missing sections, since those already have a highlighted block)
  const result = [];
  for (const section of sections) {
    // ignore the sections that have a [MISSING] header, we've already processed them above
    if (section.match(missingHeaderRegex)) {
      // Check if this section starts with a [MISSING] header
      const processedSection = section.replace("[MISSING]", "**[MISSING]**");
      result.push(
        `<div class="bg-orange-100 border-l-4 border-orange-500 text-orange-700 pb-1 px-3 rounded-md my-4"><div class="prose-sm max-w-none prose-ul:mt-1 prose-li:mt-0.5 ${highlightedTextStyles} pt-3 prose-headings:mb-2">\n\n${processedSection}\n</div></div>\n`
      );
      continue;
    }
    const lines = section.split("\n");
    let inMissingListItem = false;
    let missingItemIndent = 0;
    let missingItemLines = [];
    const listItemRegex = /^([-*+]|\d+\.)/;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trimStart();
      const indentLevel = line.length - trimmedLine.length;

      // Check if this is a [MISSING] list item
      if (trimmedLine.match(/^([-*+]|\d+\.)\s+\[MISSING\]/)) {
        // Start a new missing item block
        if (inMissingListItem) {
          // If we were already in a missing item, ignore because we don't want nested missing highlighted blocks (there should only be one)
          continue;
        }

        inMissingListItem = true;
        missingItemIndent = indentLevel;
        missingItemLines.push(line);
      }
      // Check if this is a sub-bullet of the current [MISSING] item (it is a child of the missing list item)
      else if (
        inMissingListItem &&
        trimmedLine.match(listItemRegex) &&
        indentLevel > missingItemIndent
      ) {
        // Add this sub-bullet to the current missing item
        missingItemLines.push(line);
      }
      // Check if this is a continuation line of the current list item or sub-bullet (it's part of the same block as the missing list item)
      else if (
        inMissingListItem &&
        !trimmedLine.match(listItemRegex) &&
        indentLevel >= missingItemIndent
      ) {
        // Add this continuation line to the current missing item
        missingItemLines.push(line);
      }
      // Otherwise, this is not part of the current missing item
      else {
        // If we were in a missing item, close it
        if (inMissingListItem) {
          const block = wrapMissingListItem(
            missingItemLines,
            missingItemIndent
          );
          result.push(block);
          missingItemLines = [];
          inMissingListItem = false;
        }

        // Add this line as-is
        result.push(line);
      }
    }

    // Don't forget to close the last missing item if there is one
    if (inMissingListItem) {
      const block = wrapMissingListItem(missingItemLines, missingItemIndent);
      result.push(block);
    }
  }
  return result.join("\n");
};

type LineType = "empty" | "header" | "paragraph" | "list";

const StandardListIndentation = 4;

// NOTE: this will need additional testing for content that isn't a blog post outline
export const reverseProcessMarkdownContent = (content: string) => {
  // Step 1: Find all orange divs (i.e. missing blocks)
  const orangeDivRegex =
    /<div[^>]*>[\s\S]*?<div[^>]*>([\s\S]*?)<\/div>[\s\S]*?<\/div>/g;
  let result = content;

  // Reverse some processing done by the markdown editor library that adds spacing and indentation to the div tags
  // e.g.
  // <div>
  //   <div>
  //     ## Markdown here
  //   </div>
  // </div>
  // In this example, there's 4 extra spaces ahead of the header, so we need to remove them (the 4 extra spaces are applied to all content inside the inner div)

  // Replace each orange div with its processed content
  // `match` is the entire orange string, including the outer div tags
  result = result.replace(orangeDivRegex, (match, divContent: string) => {
    // `innerDivMatch` is the array of matches for the inner div tags
    // [0] would be the entire match, [1] is the inner div opening tag (e.g. <div[^>]*>)
    const innerDivMatch = match.match(/<div[^>]*>[\s\S]*?(<div[^>]*>)/);
    if (!innerDivMatch) return divContent; // Shouldn't happen, but just in case
    const innerDivTag = innerDivMatch[1];
    // find the position of the inner div tag in the overall outer div string (`match`)
    const innerDivPosition = match.indexOf(innerDivTag);
    // Get the text before the inner div tag to check for newlines and indentation
    const textBeforeInnerDiv = match.substring(0, innerDivPosition);
    const lastNewlineBeforeInnerDiv = textBeforeInnerDiv.lastIndexOf("\n");

    let innerDivIndentation = 0;
    if (lastNewlineBeforeInnerDiv !== -1) {
      // There's a newline before the inner div, check the indentation before the inner div
      const indentedText = textBeforeInnerDiv.substring(
        lastNewlineBeforeInnerDiv + 1
      );
      innerDivIndentation = indentedText.length;
    }
    if (innerDivIndentation === 0) {
      // we don't need to do anything, indentation has not been applied
      return divContent;
    }

    // now we know that the inner div has extra indentation, so we need to remove it from the content
    // Process the content inside the div
    const lines = divContent.split("\n");
    const processedLines = [];

    // Process each line to remove the base indentation
    for (const line of lines) {
      if (line.trim() === "") {
        processedLines.push("");
        continue;
      }

      // Remove the base indentation from each line
      const totalIndent = line.match(/^(\s*)/)?.[1];
      if (totalIndent && totalIndent.length >= innerDivIndentation) {
        // Keep the relative indentation - the extra indentation is always 4 spaces
        const relativeIndent = totalIndent.substring(StandardListIndentation);
        const correctlyIndentedLine = `${relativeIndent}${line.trimStart()}`;
        processedLines.push(correctlyIndentedLine);
      } else {
        processedLines.push(line.trimStart());
      }
    }

    return processedLines.join("\n");
  });

  // Step 2: Replace all variations of **[MISSING]** with [MISSING] (**\\[MISSING]**, **\[MISSING]**, **[MISSING]**, \[MISSING])
  // This handles the backslash and bold tags that might be put in by the markdown editor
  result = result.replace(
    /\*\*\\\\\[MISSING\]\*\*|\*\*\\\[MISSING\]\*\*|\*\*\[MISSING\]\*\*|\\\[MISSING\]/g,
    "[MISSING]"
  );

  // Step 3: Split the content into lines for processing, fix indentation in lists (4 spaces for each level of nesting)
  const lines = result.split("\n");
  const processedLines = [];
  let previousLineType: LineType = "empty";
  let listIndentationLevels: number[] = []; // a map of nesting level to the indentation level of list items in the markdown

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimEnd(); // only trim the end to keep track of indentation
    const trimmedLine = line.trim();

    // Skip empty lines but track them (ignore empty lines between list items)
    if (trimmedLine === "") {
      let isNextBlockAListItem = false;
      let j = i + 1;
      while (j < lines.length) {
        if (lines[j].trim().match(/^(\d+\.|[-*])\s/)) {
          isNextBlockAListItem = true;
          break;
        } else if (lines[j].trim() === "") {
          j++;
        } else {
          break;
        }
      }
      // Only add one empty line at a time, and only if the previous line is not a list item and the next line is not a list item
      if (
        previousLineType !== "empty" &&
        !(previousLineType === "list" && isNextBlockAListItem)
      ) {
        processedLines.push("");
        previousLineType = "empty";
      }
      continue;
    }

    // Check if this is a list item at any level
    const listItemMatch = line.match(/^(\s*)([-*+]|\d+\.)\s/);

    // If this is a list item, we need to fix the indentation
    // the library returns nested lists with inconsistent indentation (sometimes 2 spaces, sometimes 3, sometimes 4)
    // we build a map that tracks the intended indentation level for each nesting level
    // then we can figure out what the intended nesting level is for the current list item and multiply it by 4 to get the correct indentation
    if (listItemMatch) {
      const currentIndent = listItemMatch[1].length;
      const listMarker = listItemMatch[2];
      const contentAfterMarker = line.substring(
        currentIndent + listMarker.length
      );

      // Determine the nesting level based on indentation
      let nestingLevel = 0;

      if (listIndentationLevels.length === 0) {
        // This is a top-level list item
        listIndentationLevels = [currentIndent];
        nestingLevel = 0;
      } else {
        // Find the intended level of nesting for this list item
        for (let j = 0; j < listIndentationLevels.length; j++) {
          if (currentIndent > listIndentationLevels[j]) {
            nestingLevel = j + 1;
          }
        }

        // Update the indentation levels array, to track the latest indentation level for each nesting level
        if (nestingLevel >= listIndentationLevels.length) {
          // This is a new nesting level
          listIndentationLevels.push(currentIndent);
        } else if (currentIndent < listIndentationLevels[nestingLevel]) {
          // We're at an existing level but with different indentation, so update this level and remove all further levels of nesting
          listIndentationLevels = listIndentationLevels.slice(
            0,
            nestingLevel + 1
          );
          listIndentationLevels[nestingLevel] = currentIndent;
        }
      }

      // Create the correct indentation (4 spaces per nesting level)
      const correctIndent =
        nestingLevel === 0
          ? ""
          : " ".repeat(nestingLevel * StandardListIndentation);

      // Reconstruct the line with proper indentation
      const correctedLine = correctIndent + listMarker + contentAfterMarker;

      processedLines.push(correctedLine);
      previousLineType = "list";
      continue;
    } else {
      // Not a list item, reset the indentation tracking
      listIndentationLevels = [];
    }

    // Determine the type of the current line
    let currentLineType: LineType;
    if (trimmedLine.match(/^#{1,6}\s/)) {
      currentLineType = "header";
    } else if (trimmedLine.match(/^(\d+\.|[-*])\s/)) {
      currentLineType = "list";
    } else {
      currentLineType = "paragraph";
    }

    // Add the current line
    processedLines.push(line);
    previousLineType = currentLineType;
  }

  // Join the processed lines back together
  return processedLines.join("\n");
};
